import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useParties } from "@/contexts/PartyContext";
import { useAccounting } from "@/contexts/AccountingContext";
import { Party, PartyType, UpdatePartyInput } from "@/types/party";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QuickPartyModal } from "@/components/accounting/QuickPartyModal";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  Building2,
  Store,
  Truck,
  Phone,
  Mail,
  MapPin,
  Eye,
  Edit,
  Archive,
  RotateCcw,
  Download,
  Plus,
  Search,
  ShieldCheck,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  CheckCircle2,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  CreditCard,
  Calendar,
  AlertCircle,
} from "lucide-react";
import {
  INDIAN_STATES_WITH_GST_CODES,
  PAN_ENTITY_TYPES,
} from "@/lib/onboarding/gstUtils";

const fmt = (n: number) =>
  `₹${Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function PartiesPage() {
  const {
    parties,
    loading,
    refreshParties,
    updateParty,
    archiveParty,
    restoreParty,
    getPartyById,
  } = useParties();
  const { ledgers } = useAccounting();
  const { toast } = useToast();

  const location = useLocation();
  const isCustomersView = location.pathname.includes("/customers");
  const isSuppliersView = location.pathname.includes("/suppliers");

  // Filters & Search
  const getTabFromPath = (path: string) => {
    if (path.includes("/customers") || path.includes("/customer")) return "customers";
    if (path.includes("/suppliers") || path.includes("/supplier")) return "suppliers";
    return "all";
  };

  const [activeTab, setActiveTab] = useState<string>(() => getTabFromPath(location.pathname));
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("name-asc");

  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    if (tab !== "all") {
      setActiveTab(tab);
    }
  }, [location.pathname]);

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewPartyId, setViewPartyId] = useState<string | null>(null);
  const [viewPartyData, setViewPartyData] = useState<any | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [editParty, setEditParty] = useState<Party | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Copied toast state
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Load detailed party when viewing
  useEffect(() => {
    if (!viewPartyId) {
      setViewPartyData(null);
      return;
    }
    let cancelled = false;
    setViewLoading(true);

    getPartyById(viewPartyId)
      .then((data) => {
        if (!cancelled) setViewPartyData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({
            title: "Error Loading Details",
            description: err.message,
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setViewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [viewPartyId, getPartyById, toast]);

  // Copy to clipboard helper
  const copyToClipboard = (text: string, label: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast({ title: "Copied", description: `${label} copied to clipboard.` });
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Metrics
  const metrics = useMemo(() => {
    const active = parties.filter((p) => p.status === "active");
    const archived = parties.filter((p) => p.status === "archived");

    const customers = active.filter((p) => p.partyType === "customer" || p.partyType === "both");
    const suppliers = active.filter((p) => p.partyType === "supplier" || p.partyType === "both");
    const both = active.filter((p) => p.partyType === "both");

    let totalReceivable = 0;
    let totalPayable = 0;

    active.forEach((p) => {
      if (p.partyType === "customer") {
        if (p.currentBalance > 0) totalReceivable += p.currentBalance;
      } else if (p.partyType === "supplier") {
        if (p.currentBalance > 0) totalPayable += p.currentBalance;
      } else if (p.partyType === "both") {
        // Dual mapped
        if (p.debtorCurrentBalance && p.debtorCurrentBalance > 0) totalReceivable += p.debtorCurrentBalance;
        if (p.creditorCurrentBalance && p.creditorCurrentBalance > 0) totalPayable += p.creditorCurrentBalance;
      }
    });

    return {
      total: parties.length,
      activeCount: active.length,
      archivedCount: archived.length,
      customersCount: customers.length,
      suppliersCount: suppliers.length,
      bothCount: both.length,
      totalReceivable,
      totalPayable,
      netWorkingCapital: totalReceivable - totalPayable,
    };
  }, [parties]);

  // Filtering & Sorting
  const filteredParties = useMemo(() => {
    return parties
      .filter((p) => {
        // Tab filter
        if (activeTab === "customers" && p.partyType !== "customer" && p.partyType !== "both") return false;
        if (activeTab === "suppliers" && p.partyType !== "supplier" && p.partyType !== "both") return false;
        if (activeTab === "both" && p.partyType !== "both") return false;
        if (activeTab === "archived" && p.status !== "archived") return false;
        if (activeTab !== "archived" && p.status === "archived") return false;

        // State filter
        if (stateFilter !== "all") {
          const pState = p.billingState || p.shippingState || "";
          if (pState.toLowerCase() !== stateFilter.toLowerCase()) return false;
        }

        // Search query
        if (search.trim()) {
          const q = search.toLowerCase().trim();
          const matches =
            (p.name || "").toLowerCase().includes(q) ||
            (p.displayName && p.displayName.toLowerCase().includes(q)) ||
            (p.gstin && p.gstin.toLowerCase().includes(q)) ||
            (p.pan && p.pan.toLowerCase().includes(q)) ||
            (p.phone && p.phone.toLowerCase().includes(q)) ||
            (p.email && p.email.toLowerCase().includes(q)) ||
            (p.contactPerson && p.contactPerson.toLowerCase().includes(q)) ||
            (p.billingCity && p.billingCity.toLowerCase().includes(q));
          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === "name-asc") return (a.name || "").localeCompare(b.name || "");
        if (sortBy === "name-desc") return (b.name || "").localeCompare(a.name || "");
        if (sortBy === "balance-high") return (b.currentBalance || 0) - (a.currentBalance || 0);
        if (sortBy === "balance-low") return (a.currentBalance || 0) - (b.currentBalance || 0);
        if (sortBy === "recent") return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
        return 0;
      });
  }, [parties, activeTab, stateFilter, search, sortBy]);

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredParties.length === 0) {
      toast({ title: "Nothing to Export", description: "No parties match current filter." });
      return;
    }

    const headers = [
      "ID",
      "Name",
      "Type",
      "Contact Person",
      "Phone",
      "Email",
      "GST Registered",
      "GSTIN",
      "PAN",
      "Business Constitution",
      "Billing Address",
      "Billing City",
      "Billing State",
      "Billing PIN",
      "Outstanding Balance",
      "Balance Type",
      "Credit Limit",
      "Payment Terms",
      "Status",
    ];

    const rows = filteredParties.map((p) => [
      `"${p.id}"`,
      `"${(p.name || "").replace(/"/g, '""')}"`,
      `"${p.partyType}"`,
      `"${(p.contactPerson || "").replace(/"/g, '""')}"`,
      `"${p.phone || ""}"`,
      `"${p.email || ""}"`,
      p.gstRegistered ? "Yes" : "No",
      `"${p.gstin || ""}"`,
      `"${p.pan || ""}"`,
      `"${p.businessConstitution || ""}"`,
      `"${(p.billingAddress || "").replace(/"/g, '""')}"`,
      `"${p.billingCity || ""}"`,
      `"${p.billingState || ""}"`,
      `"${p.billingPincode || ""}"`,
      p.currentBalance || 0,
      p.balanceType,
      p.creditLimit || 0,
      `"${p.paymentTerms || ""}"`,
      p.status,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `parties_export_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: `Exported ${filteredParties.length} parties to CSV.`,
    });
  };

  // Archive / Restore Handlers
  const handleToggleStatus = async (party: Party) => {
    try {
      if (party.status === "active") {
        await archiveParty(party.id);
        toast({
          title: "Party Archived",
          description: `"${party.name}" has been archived.`,
        });
      } else {
        await restoreParty(party.id);
        toast({
          title: "Party Restored",
          description: `"${party.name}" is now active.`,
        });
      }
    } catch (err: any) {
      toast({
        title: "Action Failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  // Edit Save Handler
  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editParty) return;

    if (!editParty.name.trim()) {
      toast({ title: "Name Required", description: "Party name cannot be empty.", variant: "destructive" });
      return;
    }

    setEditSaving(true);
    try {
      const payload: UpdatePartyInput = {
        name: editParty.name.trim(),
        displayName: editParty.displayName?.trim() || editParty.name.trim(),
        partyType: editParty.partyType,
        entityType: editParty.entityType,
        contactPerson: editParty.contactPerson?.trim() || undefined,
        phone: editParty.phone?.trim() || undefined,
        email: editParty.email?.trim() || undefined,
        gstRegistered: editParty.gstRegistered,
        gstin: editParty.gstRegistered && editParty.gstin ? editParty.gstin.trim() : undefined,
        pan: editParty.pan ? editParty.pan.trim() : undefined,
        businessConstitution: editParty.businessConstitution,
        billingAddress: editParty.billingAddress?.trim() || undefined,
        billingCity: editParty.billingCity?.trim() || undefined,
        billingState: editParty.billingState,
        billingPincode: editParty.billingPincode?.trim() || undefined,
        shippingAddress: editParty.shippingAddress?.trim() || undefined,
        shippingCity: editParty.shippingCity?.trim() || undefined,
        shippingState: editParty.shippingState,
        shippingPincode: editParty.shippingPincode?.trim() || undefined,
        creditLimit: Number(editParty.creditLimit) || 0,
        paymentTerms: editParty.paymentTerms,
        notes: editParty.notes?.trim() || undefined,
      };

      await updateParty(editParty.id, payload);
      toast({
        title: "Party Updated",
        description: `"${editParty.name}" details saved successfully.`,
      });
      setEditParty(null);
    } catch (err: any) {
      toast({
        title: "Update Failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display tracking-tight text-foreground flex items-center gap-2.5">
            <Users className="h-6 w-6 text-primary" />
            <span>{isCustomersView ? "Customers & Clients" : isSuppliersView ? "Suppliers & Vendors" : "Parties"}</span>
            <span className="text-sm font-normal text-muted-foreground hidden sm:inline">
              {isCustomersView ? "(Client Accounts & Receivables)" : isSuppliersView ? "(Vendor Accounts & Payables)" : "(Customers & Suppliers)"}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isCustomersView
              ? "Manage your business customers, client accounts, GST information, credit limits and outstanding receivables."
              : isSuppliersView
              ? "Manage your vendor relationships, purchase accounts, payment terms and accounts payable."
              : "Unified commercial directory with real-time double-entry ledger balance integration"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="text-xs h-9 gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export CSV</span>
          </Button>

          <Button
            size="sm"
            onClick={() => setAddModalOpen(true)}
            className="text-xs h-9 gap-1.5 bg-primary text-primary-foreground font-semibold shadow-sm"
          >
            <Plus className="h-4 w-4" />
            <span>{isCustomersView ? "Add Customer" : isSuppliersView ? "Add Supplier" : "Add Party"}</span>
          </Button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Active Parties */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>Total Parties</span>
              <Building2 className="h-4 w-4 text-muted-foreground/60" />
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold text-foreground">{metrics.activeCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.bothCount > 0 ? `${metrics.bothCount} dual-role · ` : ""}
              {metrics.archivedCount} archived
            </p>
          </CardContent>
        </Card>

        {/* Customers & Receivables */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>Receivables (Customers)</span>
              <ArrowDownLeft className="h-4 w-4 text-blue-500" />
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {fmt(metrics.totalReceivable)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {metrics.customersCount} customer accounts
            </p>
          </CardContent>
        </Card>

        {/* Suppliers & Payables */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>Payables (Suppliers)</span>
              <ArrowUpRight className="h-4 w-4 text-amber-500" />
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {fmt(metrics.totalPayable)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {metrics.suppliersCount} supplier accounts
            </p>
          </CardContent>
        </Card>

        {/* Net Balance */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>Net Party Balance</span>
              <CreditCard className="h-4 w-4 text-emerald-500" />
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div
              className={`text-2xl font-bold ${
                metrics.netWorkingCapital >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-rose-600 dark:text-rose-400"
              }`}
            >
              {fmt(metrics.netWorkingCapital)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.netWorkingCapital >= 0 ? "Net Receivable (Asset Dr)" : "Net Payable (Liability Cr)"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs & Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border/80 pb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
            <TabsList className="bg-muted/70 p-1">
              <TabsTrigger value="all" className="text-xs gap-1.5">
                All ({parties.filter((p) => p.status === "active").length})
              </TabsTrigger>
              <TabsTrigger value="customers" className="text-xs gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Customers ({parties.filter((p) => (p.partyType === "customer" || p.partyType === "both") && p.status === "active").length})
              </TabsTrigger>
              <TabsTrigger value="suppliers" className="text-xs gap-1.5">
                <Truck className="h-3.5 w-3.5" />
                Suppliers ({parties.filter((p) => (p.partyType === "supplier" || p.partyType === "both") && p.status === "active").length})
              </TabsTrigger>
              <TabsTrigger value="both" className="text-xs gap-1.5">
                <Store className="h-3.5 w-3.5" />
                Both ({parties.filter((p) => p.partyType === "both" && p.status === "active").length})
              </TabsTrigger>
              <TabsTrigger value="archived" className="text-xs gap-1.5">
                <Archive className="h-3.5 w-3.5" />
                Archived ({metrics.archivedCount})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search name, GSTIN, PAN, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs bg-card"
              />
            </div>

            {/* State filter */}
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="h-8 text-xs w-[130px] bg-card">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                <SelectItem value="all">All States</SelectItem>
                {INDIAN_STATES_WITH_GST_CODES.map((s) => (
                  <SelectItem key={s.code} value={s.name} className="text-xs">
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort order */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs w-[140px] bg-card">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                <SelectItem value="balance-high">Highest Balance</SelectItem>
                <SelectItem value="balance-low">Lowest Balance</SelectItem>
                <SelectItem value="recent">Recently Updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Parties Data Table */}
        <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead className="font-semibold text-xs">Party / Business</TableHead>
                <TableHead className="font-semibold text-xs">Role</TableHead>
                <TableHead className="font-semibold text-xs">GSTIN / PAN</TableHead>
                <TableHead className="font-semibold text-xs">Contact Info</TableHead>
                <TableHead className="font-semibold text-xs">Location</TableHead>
                <TableHead className="font-semibold text-xs text-right">Ledger Balance</TableHead>
                <TableHead className="font-semibold text-xs text-center">Status</TableHead>
                <TableHead className="font-semibold text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                      <p className="text-xs">Loading parties from database...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredParties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center text-muted-foreground">
                        <Users className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">No parties found</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {search
                            ? `No customer or supplier matched "${search}".`
                            : "Get started by adding your first business customer or supplier."}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setAddModalOpen(true)}
                        className="text-xs h-8 gap-1.5 bg-primary text-primary-foreground font-semibold"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add New Party</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredParties.map((party) => {
                  const roleBadge =
                    party.partyType === "both" ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30 font-medium">
                        Both (Dual)
                      </Badge>
                    ) : party.partyType === "customer" ? (
                      <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/30 font-medium">
                        Customer
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-600 border-purple-500/30 font-medium">
                        Supplier
                      </Badge>
                    );

                  return (
                    <TableRow key={party.id} className="hover:bg-muted/30 transition-colors">
                      {/* Name & Constitution */}
                      <TableCell className="font-medium text-foreground py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-xs uppercase flex-shrink-0">
                            {((party.displayName || party.name || "?").charAt(0) || "?").toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-foreground text-xs truncate max-w-[200px]" title={party.displayName || party.name || ""}>
                                {party.displayName || party.name || "Unnamed Party"}
                              </span>
                              {party.entityType === "individual" && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 bg-sky-500/10 text-sky-600 border-sky-500/20 font-normal">
                                  Individual
                                </Badge>
                              )}
                            </div>
                            {party.displayName && party.displayName !== party.name && (
                              <div className="text-[10px] text-muted-foreground truncate max-w-[200px]">
                                {party.name}
                              </div>
                            )}
                            {party.contactPerson && (
                              <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                                c/o {party.contactPerson}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Party Type */}
                      <TableCell className="py-3">{roleBadge}</TableCell>

                      {/* GSTIN / PAN */}
                      <TableCell className="py-3">
                        {party.gstRegistered && party.gstin ? (
                          <div className="flex items-center gap-1.5">
                            <code className="text-[11px] font-mono bg-muted/70 px-1.5 py-0.5 rounded border border-border/60">
                              {party.gstin}
                            </code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(party.gstin!, "GSTIN", `gstin-${party.id}`)}
                              className="text-muted-foreground hover:text-foreground p-0.5 rounded"
                              title="Copy GSTIN"
                            >
                              {copiedKey === `gstin-${party.id}` ? (
                                <Check className="h-3 w-3 text-emerald-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        ) : party.pan ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-muted-foreground uppercase font-semibold">PAN:</span>
                            <code className="text-[11px] font-mono bg-muted/70 px-1.5 py-0.5 rounded border border-border/60">
                              {party.pan}
                            </code>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground italic">Unregistered</span>
                        )}
                      </TableCell>

                      {/* Contact Info */}
                      <TableCell className="py-3">
                        <div className="text-[11px] space-y-0.5">
                          {party.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3 text-muted-foreground/70" />
                              <span>{party.phone}</span>
                            </div>
                          )}
                          {party.email && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3 text-muted-foreground/70" />
                              <span className="truncate max-w-[150px]">{party.email}</span>
                            </div>
                          )}
                          {!party.phone && !party.email && (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </div>
                      </TableCell>

                      {/* City, State */}
                      <TableCell className="text-xs text-muted-foreground py-3">
                        {party.billingCity || party.billingState ? (
                          <span>
                            {party.billingCity ? `${party.billingCity}, ` : ""}
                            {party.billingState || ""}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </TableCell>

                      {/* Balances */}
                      <TableCell className="text-right py-3">
                        {party.partyType === "both" ? (
                          <div className="text-[11px] space-y-0.5">
                            <div className="text-blue-600 dark:text-blue-400 font-mono font-medium">
                              Dr: {fmt(party.debtorCurrentBalance ?? (party as any).debtorBalance ?? 0)}
                            </div>
                            <div className="text-purple-600 dark:text-purple-400 font-mono font-medium">
                              Cr: {fmt(party.creditorCurrentBalance ?? (party as any).creditorBalance ?? 0)}
                            </div>
                          </div>
                        ) : (
                          <div
                            className={`font-mono text-xs font-semibold ${
                              party.partyType === "customer"
                                ? party.currentBalance > 0
                                  ? "text-blue-600 dark:text-blue-400"
                                  : "text-muted-foreground"
                                : party.currentBalance > 0
                                ? "text-purple-600 dark:text-purple-400"
                                : "text-muted-foreground"
                            }`}
                          >
                            {fmt(party.currentBalance || 0)}{" "}
                            <span className="text-[10px] font-normal text-muted-foreground">
                              {party.balanceType}
                            </span>
                          </div>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell className="text-center py-3">
                        <Badge
                          variant={party.status === "active" ? "default" : "secondary"}
                          className={`text-[10px] capitalize font-medium ${
                            party.status === "active"
                              ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {party.status}
                        </Badge>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => setViewPartyId(party.id)}
                            title="View Profile & Ledger History"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => setEditParty({ ...party })}
                            title="Edit Party Details"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${
                              party.status === "active"
                                ? "text-muted-foreground hover:text-rose-500"
                                : "text-emerald-500 hover:text-emerald-600"
                            }`}
                            onClick={() => handleToggleStatus(party)}
                            title={party.status === "active" ? "Archive Party" : "Restore Party"}
                          >
                            {party.status === "active" ? (
                              <Archive className="h-3.5 w-3.5" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Quick Add Party Modal */}
      <QuickPartyModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        partyType={isCustomersView || activeTab === "customers" ? "customer" : isSuppliersView || activeTab === "suppliers" ? "supplier" : "customer"}
        onPartyCreated={() => {
          refreshParties();
        }}
      />

      {/* View Party Details Dialog / Drawer */}
      <Dialog open={!!viewPartyId} onOpenChange={(open) => !open && setViewPartyId(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto p-4 sm:p-6">
          {viewLoading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-2">
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground">Loading party profile and vouchers...</p>
            </div>
          ) : viewPartyData ? (
            <div className="space-y-5">
              <DialogHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <DialogTitle className="text-lg font-bold font-display text-foreground flex items-center gap-2">
                      <span>{viewPartyData.party.name}</span>
                      <Badge
                        variant="outline"
                        className="text-[10px] font-medium uppercase tracking-wider"
                      >
                        {viewPartyData.party.partyType}
                      </Badge>
                      <Badge
                        className={`text-[10px] ${
                          viewPartyData.party.status === "active"
                            ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {viewPartyData.party.status}
                      </Badge>
                    </DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                      Business ID: <code className="font-mono text-[11px]">{viewPartyData.party.id}</code>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Grid: Tax, Contact, Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs bg-muted/30 p-3.5 rounded-lg border border-border/70">
                {/* Contact & Tax Column */}
                <div className="space-y-2.5">
                  <h4 className="font-semibold text-foreground uppercase tracking-wider text-[11px]">
                    Tax & Business Identification
                  </h4>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">GSTIN Status</span>
                    <span className="font-medium text-foreground">
                      {viewPartyData.party.gstRegistered ? "Registered Taxpayer" : "Unregistered"}
                    </span>
                  </div>
                  {viewPartyData.party.gstin && (
                    <div>
                      <span className="text-muted-foreground block text-[10px]">GSTIN</span>
                      <code className="font-mono bg-card px-1.5 py-0.5 rounded border border-border text-[11px]">
                        {viewPartyData.party.gstin}
                      </code>
                    </div>
                  )}
                  {viewPartyData.party.pan && (
                    <div>
                      <span className="text-muted-foreground block text-[10px]">PAN</span>
                      <code className="font-mono bg-card px-1.5 py-0.5 rounded border border-border text-[11px]">
                        {viewPartyData.party.pan}
                      </code>
                    </div>
                  )}
                  {viewPartyData.party.businessConstitution && (
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Constitution</span>
                      <span className="font-medium text-foreground">
                        {viewPartyData.party.businessConstitution}
                      </span>
                    </div>
                  )}
                </div>

                {/* Contact & Credit Column */}
                <div className="space-y-2.5">
                  <h4 className="font-semibold text-foreground uppercase tracking-wider text-[11px]">
                    Contact & Terms
                  </h4>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Contact Person</span>
                    <span className="font-medium text-foreground">
                      {viewPartyData.party.contactPerson || "Not specified"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Phone</span>
                    <span className="font-medium text-foreground">
                      {viewPartyData.party.phone || "Not specified"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Email</span>
                    <span className="font-medium text-foreground">
                      {viewPartyData.party.email || "Not specified"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Payment Terms & Credit Limit</span>
                    <span className="font-medium text-foreground">
                      {viewPartyData.party.paymentTerms || "Due on Receipt"} · Limit: {fmt(viewPartyData.party.creditLimit || 0)}
                    </span>
                  </div>
                </div>

                {/* Addresses full width */}
                <div className="sm:col-span-2 pt-2 border-t border-border/60 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-muted-foreground block text-[10px] font-semibold uppercase">Billing Address</span>
                    <p className="text-[11px] text-foreground mt-0.5">
                      {viewPartyData.party.billingAddress || "No street address entered"}
                      {viewPartyData.party.billingCity ? `, ${viewPartyData.party.billingCity}` : ""}
                      {viewPartyData.party.billingState ? `, ${viewPartyData.party.billingState}` : ""}
                      {viewPartyData.party.billingPincode ? ` - ${viewPartyData.party.billingPincode}` : ""}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px] font-semibold uppercase">Shipping Address</span>
                    <p className="text-[11px] text-foreground mt-0.5">
                      {viewPartyData.party.shippingAddress || "Identical to billing"}
                      {viewPartyData.party.shippingCity ? `, ${viewPartyData.party.shippingCity}` : ""}
                      {viewPartyData.party.shippingState ? `, ${viewPartyData.party.shippingState}` : ""}
                      {viewPartyData.party.shippingPincode ? ` - ${viewPartyData.party.shippingPincode}` : ""}
                    </p>
                  </div>
                </div>
              </div>

              {/* Accounting Ledgers Mapping */}
              <div className="border border-border rounded-lg p-3 space-y-2 bg-card">
                <h4 className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Linked Accounting Ledgers</span>
                  <Badge variant="secondary" className="text-[10px]">Double-Entry Mapped</Badge>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {viewPartyData.debtorLedger && (
                    <div className="p-2 rounded bg-muted/40 border border-border/60">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground text-[11px]">Sundry Debtors Ledger</span>
                        <span className="font-mono text-blue-600 font-bold">
                          {fmt(viewPartyData.debtorLedger.currentBalance || 0)} Dr
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        ID: {viewPartyData.debtorLedger.id}
                      </p>
                    </div>
                  )}

                  {viewPartyData.creditorLedger && (
                    <div className="p-2 rounded bg-muted/40 border border-border/60">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground text-[11px]">Sundry Creditors Ledger</span>
                        <span className="font-mono text-purple-600 font-bold">
                          {fmt(viewPartyData.creditorLedger.currentBalance || 0)} Cr
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        ID: {viewPartyData.creditorLedger.id}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Vouchers / Transactions */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Recent Invoices & Transactions ({viewPartyData.vouchers?.length || 0})</span>
                  <span className="text-[10px] text-muted-foreground">Real-time voucher audit</span>
                </h4>

                <div className="rounded-md border border-border overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="text-[11px] py-1.5">Voucher #</TableHead>
                        <TableHead className="text-[11px] py-1.5">Date</TableHead>
                        <TableHead className="text-[11px] py-1.5">Type</TableHead>
                        <TableHead className="text-[11px] py-1.5 text-right">Amount</TableHead>
                        <TableHead className="text-[11px] py-1.5 text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!viewPartyData.vouchers || viewPartyData.vouchers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-xs text-muted-foreground">
                            No invoices or vouchers recorded for this party yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        viewPartyData.vouchers.map((v: any) => (
                          <TableRow key={v.id} className="text-xs">
                            <TableCell className="font-mono font-medium">{v.voucherNumber}</TableCell>
                            <TableCell className="text-muted-foreground">{v.date}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {v.type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {fmt(v.totalAmount)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={v.status === "Approved" ? "default" : "secondary"}
                                className="text-[10px]"
                              >
                                {v.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewPartyId(null)}
                  className="text-xs h-8"
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Party Dialog */}
      <Dialog open={!!editParty} onOpenChange={(open) => !open && setEditParty(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto p-4 sm:p-6">
          {editParty && (
            <form onSubmit={handleEditSave} className="space-y-4">
              <DialogHeader>
                <DialogTitle className="text-base font-bold font-display">
                  Edit Party Details: {editParty.name}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Update business contacts, GSTIN, billing/shipping address, and credit settings.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-1">
                {/* Name */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Business Name *</Label>
                  <Input
                    value={editParty.name}
                    onChange={(e) => setEditParty({ ...editParty, name: e.target.value })}
                    className="h-8 text-xs"
                    required
                  />
                </div>

                {/* Role */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Party Role</Label>
                  <Select
                    value={editParty.partyType}
                    onValueChange={(v) => setEditParty({ ...editParty, partyType: v as PartyType })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                      <SelectItem value="both">Both (Customer & Supplier)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* GSTIN & PAN */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">GSTIN</Label>
                    <Input
                      value={editParty.gstin || ""}
                      maxLength={15}
                      onChange={(e) => setEditParty({ ...editParty, gstin: e.target.value.toUpperCase() })}
                      className="h-8 text-xs font-mono"
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">PAN</Label>
                    <Input
                      value={editParty.pan || ""}
                      maxLength={10}
                      onChange={(e) => setEditParty({ ...editParty, pan: e.target.value.toUpperCase() })}
                      className="h-8 text-xs font-mono"
                      placeholder="AAAAA0000A"
                    />
                  </div>
                </div>

                {/* Contact & Phone */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Contact Person</Label>
                    <Input
                      value={editParty.contactPerson || ""}
                      onChange={(e) => setEditParty({ ...editParty, contactPerson: e.target.value })}
                      className="h-8 text-xs"
                      placeholder="Proprietor / Accounts manager"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Mobile Phone</Label>
                    <Input
                      value={editParty.phone || ""}
                      onChange={(e) => setEditParty({ ...editParty, phone: e.target.value })}
                      className="h-8 text-xs"
                      placeholder="9876543210"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Email Address</Label>
                  <Input
                    type="email"
                    value={editParty.email || ""}
                    onChange={(e) => setEditParty({ ...editParty, email: e.target.value })}
                    className="h-8 text-xs"
                    placeholder="accounts@company.com"
                  />
                </div>

                {/* Billing Address */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Billing Street Address</Label>
                  <Input
                    value={editParty.billingAddress || ""}
                    onChange={(e) => setEditParty({ ...editParty, billingAddress: e.target.value })}
                    className="h-8 text-xs"
                    placeholder="Plot / Street / Area"
                  />
                </div>

                {/* City, State, PIN */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">City</Label>
                    <Input
                      value={editParty.billingCity || ""}
                      onChange={(e) => setEditParty({ ...editParty, billingCity: e.target.value })}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">State</Label>
                    <Select
                      value={editParty.billingState || "Punjab"}
                      onValueChange={(v) => setEditParty({ ...editParty, billingState: v })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-48">
                        {INDIAN_STATES_WITH_GST_CODES.map((s) => (
                          <SelectItem key={s.code} value={s.name} className="text-xs">
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">PIN Code</Label>
                    <Input
                      value={editParty.billingPincode || ""}
                      maxLength={6}
                      onChange={(e) => setEditParty({ ...editParty, billingPincode: e.target.value })}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Credit Limit & Terms */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Credit Limit (₹)</Label>
                    <Input
                      type="number"
                      value={editParty.creditLimit || ""}
                      onChange={(e) => setEditParty({ ...editParty, creditLimit: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Payment Terms</Label>
                    <Select
                      value={editParty.paymentTerms || "Due on Receipt"}
                      onValueChange={(v) => setEditParty({ ...editParty, paymentTerms: v })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                        <SelectItem value="Net 15">Net 15 Days</SelectItem>
                        <SelectItem value="Net 30">Net 30 Days</SelectItem>
                        <SelectItem value="Net 45">Net 45 Days</SelectItem>
                        <SelectItem value="Net 60">Net 60 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditParty(null)}
                  disabled={editSaving}
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={editSaving}
                  className="text-xs h-8 bg-primary text-primary-foreground font-semibold gap-1.5"
                >
                  {editSaving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  <span>Save Changes</span>
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
